/* =====================================================================================
   Demonstration data gate.

   Forum shipped with worked examples in every module so the interface could be evaluated
   before Postgres existed. That was the right call then and the wrong thing to leave in:
   the moment a real backend is connected, invented opportunities, invoices and partners
   stop being illustrative and start being lies on a screen someone might act on.

   ONE RULE: if a backend is configured, demonstration rows do not appear. Not dimmed, not
   labelled, not behind a toggle. Absent.

   Wrap any hardcoded array in demo(). With no backend it returns the rows and the interface
   still demonstrates. With Supabase connected it returns nothing, and the module shows its
   real, empty state until someone enters real work.

   What is NOT demonstration data, and must never be wrapped:
     * the Atlas: 617 centres, volumes and reimbursement routes are researched fact
     * configuration: pipeline stages, claim gate rules, dunning ladders, currency rates
     * the deadline diary on Today: those are genuine dated commitments
   ===================================================================================== */

import { TENANT } from '@tenant';

/* Read straight from the environment rather than importing BACKEND from lib/db. db.ts
   imports the data files, the data files import this one, and importing db back would close
   a cycle whose resolution order decides whether BACKEND is defined yet. If it resolved the
   wrong way, BACKEND would be undefined, the check would fail open, and the demonstration
   rows would ship to a live system. Same single source of truth, no cycle. */
const HAS_BACKEND = Boolean(import.meta.env.VITE_SUPABASE_URL);

export function demo<T>(rows: T[]): T[] {
  // With a backend, the real records are authoritative and the examples must not appear.
  if (HAS_BACKEND) return [];
  // Without one, only the tenant these examples were written for may see them.
  return TENANT.usesBuiltInExamples ? rows : [];
}

/** True when the interface is running on worked examples, so a banner can say so. */
export const SHOWING_DEMO = !HAS_BACKEND;
